import type { ComponentType } from 'react';
import {
  Banknote,
  BarChart3,
  BarChart4,
  BookOpen,
  BookOpenCheck,
  Bone,
  Boxes,
  Calculator,
  Briefcase,
  Calendar,
  CalendarCheck,
  CalendarDays,
  CircleUser,
  ClipboardList,
  Coins,
  FileBarChart,
  FlaskConical,
  HeartPulse,
  History,
  Home,
  IdCard,
  Image,
  Inbox,
  Mailbox,
  MailPlus,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Microscope,
  Package,
  PieChart,
  Scan,
  Settings,
  SlidersHorizontal,
  TestTube,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  Warehouse,
  Wrench,
} from 'lucide-react';
import { i18nArray } from '@/lib/i18n-map';

/**
 * Setul de iconițe al barei laterale, ales de clinică din Setări →
 * Personalizare → Aspect (`org_config.iconSet`, adus la toată echipa prin
 * `GET /settings/look`).
 *
 * De ce aici și nu în `AppLayout`: iconițele erau scrise în `NAV`, o singură
 * variantă pentru toată lumea. Clinicile n-au aceeași imagine despre ce e „un
 * pacient" sau „imagistica" — una vrea forme neutre, alta le vrea medicale —
 * așa că fiecare intrare are acum trei desene, iar bara alege după setul
 * clinicii. Identitatea unei intrări rămâne calea ei (`to`), nu iconița: așa
 * schimbarea setului nu atinge nici permisiunile, nici ordinea meniului.
 *
 * Un `to` fără iconiță în setul ales cade pe „clasic", care le are pe toate —
 * deci o rută nouă apare cu iconița ei chiar înainte de a fi desenată în
 * celelalte seturi.
 */
type NavIcon = ComponentType<{ size?: number | string }>;

export type IconSetKey = 'classic' | 'medical' | 'geometric';

/** „Clasic" e și rezerva: are o iconiță pentru fiecare rută din meniu. */
const CLASSIC: Record<string, NavIcon> = {
  '/': LayoutDashboard,
  '/calendar': CalendarDays,
  '/inquiries': Inbox,
  '/patients': Users,
  '/payments': Wallet,
  '/inventory': Package,
  '/imaging': Scan,
  '/chat': MessageSquare,
  '/economy': TrendingUp,
  '/accounting': BookOpen,
  '/reports': BarChart3,
  '/hr': UserCog,
  '/lab': FlaskConical,
  '/journal': History,
  '/settings': Settings,
};

const MEDICAL: Record<string, NavIcon> = {
  '/': Home,
  '/calendar': CalendarCheck,
  '/inquiries': MailPlus,
  '/patients': HeartPulse,
  '/payments': Banknote,
  '/inventory': Boxes,
  '/imaging': Bone,
  '/chat': MessageCircle,
  '/economy': LineChart,
  '/accounting': BookOpenCheck,
  '/reports': FileBarChart,
  '/hr': IdCard,
  '/lab': Microscope,
  '/journal': ClipboardList,
  '/settings': SlidersHorizontal,
};

const GEOMETRIC: Record<string, NavIcon> = {
  '/': LayoutGrid,
  '/calendar': Calendar,
  '/inquiries': Mailbox,
  '/patients': CircleUser,
  '/payments': Coins,
  '/inventory': Warehouse,
  '/imaging': Image,
  '/chat': MessagesSquare,
  '/economy': PieChart,
  '/accounting': Calculator,
  '/reports': BarChart4,
  '/hr': Briefcase,
  '/lab': TestTube,
  '/journal': ClipboardList,
  '/settings': Wrench,
};

const SETS: Record<IconSetKey, Record<string, NavIcon>> = {
  classic: CLASSIC,
  medical: MEDICAL,
  geometric: GEOMETRIC,
};

/** Ce scrie `org_config.iconSet`; orice altceva (inclusiv gol) e „clasic". */
export function iconSetKey(value: string | null | undefined): IconSetKey {
  return value === 'medical' || value === 'geometric' ? value : 'classic';
}

/** Iconița unei rute în setul cerut, cu „clasic" ca rezervă. */
export function navIcon(set: string | null | undefined, to: string, size = 18) {
  const key = iconSetKey(set);
  const Icon = SETS[key][to] ?? CLASSIC[to] ?? SETS.classic['/'];
  return <Icon size={size} />;
}

/**
 * Cele trei seturi, pentru ecranul de alegere din Personalizare. `sample` sunt
 * câteva rute reprezentative, desenate ca previzualizare pe card.
 */
export const ICON_SETS: Array<{ key: IconSetKey; label: string; desc: string }> = [
  { key: 'classic', label: 'clasic', desc: 'Iconițele obișnuite ale aplicației.' },
  {
    key: 'medical',
    label: 'medical',
    desc: 'Forme mai apropiate de cabinet — puls, os, microscop.',
  },
  { key: 'geometric', label: 'geometric', desc: 'Forme simple — cercuri, felii, pătrate.' },
];

/** Rutele desenate în previzualizarea fiecărui set, în ordinea din meniu. */
export const ICON_SET_SAMPLE = ['/', '/calendar', '/patients', '/payments', '/imaging', '/lab'];

/**
 * Cât de dens e desenată bara — ales tot din Setări → Personalizare → Aspect
 * (`org_config.menuStyle`, livrat prin `look`). Nu schimbă iconițele, doar
 * cerneala din jurul lor:
 *  - `chips`   — chip pe fiecare iconiță (implicit);
 *  - `active`  — chip doar pe elementul activ, restul iconițe simple;
 *  - `compact` — doar iconițe, fără etichete (cel mai scund; numele la hover).
 * `AppLayout` pune valoarea pe `.shell__rail` ca `data-menu-style` și restul e
 * din CSS.
 */
export type MenuStyleKey = 'chips' | 'active' | 'compact';

/** Ce scrie `org_config.menuStyle`; orice altceva (inclusiv gol) e „chips". */
export function menuStyleKey(value: string | null | undefined): MenuStyleKey {
  return value === 'active' || value === 'compact' ? value : 'chips';
}

export const MENU_STYLES = i18nArray<{ key: MenuStyleKey; label: string; desc: string }>('common', [
  { key: 'chips', label: 'chipPeToate', desc: 'Fiecare iconiță într-un chip; cel activ e plin.' },
  {
    key: 'active',
    label: 'chipDoarPeActiv',
    desc: 'Doar elementul activ are chip; restul, iconițe simple.',
  },
  {
    key: 'compact',
    label: 'doarIconite',
    desc: 'Fără etichete — cel mai compact; numele apare la hover.',
  },
]);
